import { useIsMobile } from "./useIsMobile";
import Slide from "@mui/material/Slide";
import Fade from "@mui/material/Fade";
import { TransitionProps } from "@mui/material/transitions";
import { forwardRef, ReactElement, Ref } from "react";

/**
 * Slide transition for mobile fullscreen dialogs.
 * Slides up from bottom of screen.
 */
export const SlideUpTransition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Fade transition for desktop dialogs.
 */
export const FadeTransition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Fade ref={ref} {...props} />;
});

interface ResponsiveDialogConfig {
  /** Whether dialog should be fullscreen (true on mobile) */
  fullScreen: boolean;
  /** Transition component to use */
  TransitionComponent: typeof SlideUpTransition | typeof FadeTransition;
}

/**
 * Hook for responsive dialog configuration.
 * Returns fullScreen=true and slide transition on mobile,
 * fullScreen=false and fade transition on desktop.
 *
 * @example
 * const dialogConfig = useResponsiveDialog();
 * return (
 *   <Dialog
 *     open={open}
 *     onClose={onClose}
 *     fullScreen={dialogConfig.fullScreen}
 *     TransitionComponent={dialogConfig.TransitionComponent}
 *   >
 *     ...
 *   </Dialog>
 * );
 */
export function useResponsiveDialog(): ResponsiveDialogConfig {
  const isMobile = useIsMobile();

  return {
    fullScreen: isMobile,
    TransitionComponent: isMobile ? SlideUpTransition : FadeTransition,
  };
}
